import { useCallback, useId, useMemo, useRef, type KeyboardEvent } from 'react';

import './RangeSlider.css';

export type RangeSliderProps = {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  ariaLabelMin: string;
  ariaLabelMax: string;
  format?: (n: number) => string;
  disabled?: boolean;
  className?: string;
};

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

// Round to step grid relative to `min` so step boundaries land on values
// callers expect (e.g. 500_000 + N * 50_000), not on raw multiples of step.
function snapToStep(n: number, min: number, step: number): number {
  if (step <= 0) return n;
  return min + Math.round((n - min) / step) * step;
}

export function RangeSlider({
  min,
  max,
  step,
  value,
  onChange,
  ariaLabelMin,
  ariaLabelMax,
  format,
  disabled,
  className,
}: RangeSliderProps) {
  const baseId = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<'min' | 'max' | null>(null);

  const [low, high] = value;
  const fmt = format ?? ((n: number) => String(n));

  const pct = useCallback(
    (n: number) => {
      if (max === min) return 0;
      return ((n - min) / (max - min)) * 100;
    },
    [min, max],
  );

  // UB13: enforce min < max (strict). The minimum thumb cannot exceed
  // (max-thumb - step); the maximum thumb cannot drop below (min-thumb + step).
  const setLow = useCallback(
    (next: number) => {
      const snapped = snapToStep(next, min, step);
      const clamped = clamp(snapped, min, high - step);
      if (clamped !== low) onChange([clamped, high]);
    },
    [low, high, min, step, onChange],
  );

  const setHigh = useCallback(
    (next: number) => {
      const snapped = snapToStep(next, min, step);
      const clamped = clamp(snapped, low + step, max);
      if (clamped !== high) onChange([low, clamped]);
    },
    [low, high, min, max, step, onChange],
  );

  const handleKey = (which: 'min' | 'max') => (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const cur = which === 'min' ? low : high;
    const setter = which === 'min' ? setLow : setHigh;
    const big = step * 10;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        setter(cur + step);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        setter(cur - step);
        break;
      case 'PageUp':
        event.preventDefault();
        setter(cur + big);
        break;
      case 'PageDown':
        event.preventDefault();
        setter(cur - big);
        break;
      case 'Home':
        event.preventDefault();
        setter(min);
        break;
      case 'End':
        event.preventDefault();
        setter(max);
        break;
      default:
        break;
    }
  };

  const positionFromPointer = useCallback(
    (clientX: number): number => {
      const track = trackRef.current;
      if (!track) return min;
      const rect = track.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      return min + ratio * (max - min);
    },
    [min, max],
  );

  const onPointerDown = (which: 'min' | 'max') => (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    dragRef.current = which;
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const raw = positionFromPointer(event.clientX);
    if (dragRef.current === 'min') setLow(raw);
    else setHigh(raw);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    (event.target as HTMLElement).releasePointerCapture(event.pointerId);
    dragRef.current = null;
  };

  const trackStyle = useMemo(
    () => ({ left: `${pct(low)}%`, right: `${100 - pct(high)}%` }),
    [low, high, pct],
  );

  return (
    <div
      className={['range-slider', disabled ? 'range-slider--disabled' : null, className]
        .filter(Boolean)
        .join(' ')}
      data-testid={`range-slider-${baseId}`}
    >
      <div className="range-slider__track" ref={trackRef}>
        <div className="range-slider__fill" style={trackStyle} />
        <div
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabelMin}
          aria-valuemin={min}
          aria-valuemax={high - step}
          aria-valuenow={low}
          aria-valuetext={fmt(low)}
          aria-disabled={disabled || undefined}
          className="range-slider__thumb range-slider__thumb--min"
          style={{ left: `${pct(low)}%` }}
          onKeyDown={handleKey('min')}
          onPointerDown={onPointerDown('min')}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          data-testid="range-slider-thumb-min"
        />
        <div
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabelMax}
          aria-valuemin={low + step}
          aria-valuemax={max}
          aria-valuenow={high}
          aria-valuetext={fmt(high)}
          aria-disabled={disabled || undefined}
          className="range-slider__thumb range-slider__thumb--max"
          style={{ left: `${pct(high)}%` }}
          onKeyDown={handleKey('max')}
          onPointerDown={onPointerDown('max')}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          data-testid="range-slider-thumb-max"
        />
      </div>
      <div className="range-slider__values" aria-hidden="true">
        <span>{fmt(low)}</span>
        <span>{fmt(high)}</span>
      </div>
    </div>
  );
}
